import { useState } from 'react'
import { ACCOUNT_ROLES, type Account, type AccountRole } from '../../pages/admin/accountStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

/**
 * An account's name and role. Email is the sign-in address and is not
 * changed here. An administrator's own role is fixed, so no one demotes
 * themselves out of the ability to undo it.
 */
export default function AccountEditForm({
  account,
  isSelf,
  saving,
  error,
  onSave,
  onCancel,
}: {
  account: Account
  isSelf: boolean
  saving: boolean
  error: string | null
  onSave: (values: { firstName: string; lastName: string; role: AccountRole }) => void
  onCancel: () => void
}) {
  const [firstName, setFirstName] = useState(account.firstName)
  const [lastName, setLastName] = useState(account.lastName)
  const [role, setRole] = useState<AccountRole>(account.role)
  const isAgent = account.role === 'agent'

  const changed = firstName.trim() !== account.firstName || lastName.trim() !== account.lastName || role !== account.role
  const canSave = firstName.trim() !== '' && lastName.trim() !== '' && changed

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={`Edit ${account.firstName} ${account.lastName}`}
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave && !saving) onSave({ firstName: firstName.trim(), lastName: lastName.trim(), role })
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="edit-first-name" className={LABEL}>
            First name
          </label>
          <input id="edit-first-name" type="text" maxLength={100} value={firstName} onChange={(event) => setFirstName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="edit-last-name" className={LABEL}>
            Last name
          </label>
          <input id="edit-last-name" type="text" maxLength={100} value={lastName} onChange={(event) => setLastName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="edit-role" className={LABEL}>
            Role
          </label>
          <select id="edit-role" value={role} disabled={isSelf || isAgent} onChange={(event) => setRole(event.target.value as AccountRole)} className={`${FIELD} disabled:opacity-60`}>
            {ACCOUNT_ROLES.filter((option) => isAgent || option.key !== 'agent').map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          {isSelf && <p className="mt-1 text-caption text-ink-subtle">You cannot change your own role.</p>}
          {isAgent && !isSelf && (
            <p className="mt-1 text-caption text-ink-subtle">An agent account keeps its role. For another role, deactivate it and create a new account.</p>
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
